import { useRef } from 'react';
import {
  PX_PER_SECOND,
  formatTime,
  pixelsToSeconds,
  secondsToPixels,
  snapTime,
} from '../media/editor-utils';
import type { StudioClip, TrackKind } from '../media/types';
import { useStudio } from '../store/studio-store';

const GUTTER = 200;
const TRACK_HEIGHT = 44;

const TRACK_COLORS: Record<TrackKind, string> = {
  video: '#4f46e5',
  audio: '#059669',
  text: '#b45309',
};

interface DragState {
  clipId: string;
  trackId: string;
  mode: 'move' | 'trim-start' | 'trim-end';
  startX: number;
  origStart: number;
  origDuration: number;
}

export function Timeline() {
  const project = useStudio((s) => s.project);
  const currentFrame = useStudio((s) => s.currentFrame);
  const seekFrame = useStudio((s) => s.seekFrame);
  const tracks = useStudio((s) => s.tracks);
  const assets = useStudio((s) => s.assets);
  const isPlaying = useStudio((s) => s.isPlaying);
  const loop = useStudio((s) => s.loop);
  const togglePlay = useStudio((s) => s.togglePlay);
  const setLoop = useStudio((s) => s.setLoop);
  const selectedNodeId = useStudio((s) => s.selectedNodeId);
  const selectNode = useStudio((s) => s.selectNode);
  const addClip = useStudio((s) => s.addClip);
  const updateClip = useStudio((s) => s.updateClip);
  const commitEdit = useStudio((s) => s.commitEdit);
  const importFiles = useStudio((s) => s.importFiles);
  const undo = useStudio((s) => s.undo);
  const redo = useStudio((s) => s.redo);
  const addTrack = useStudio((s) => s.addTrack);
  const canUndo = useStudio((s) => s.canUndo);
  const canRedo = useStudio((s) => s.canRedo);

  const dragRef = useRef<DragState | null>(null);

  const fps = project?.format.fps ?? 24;
  const dslDuration = project?.durationSeconds ?? 0;
  const duration = tracks.reduce(
    (max, track) => track.clips.reduce((m, c) => Math.max(m, c.start + c.duration), max),
    dslDuration
  );
  const totalWidth = GUTTER + Math.max(1, duration) * PX_PER_SECOND;
  const currentTime = currentFrame / fps;

  const assetName = (assetId: string) => assets.find((a) => a.id === assetId)?.name ?? assetId;

  function seekFromEvent(clientX: number) {
    const frame = Math.max(0, Math.round(pixelsToSeconds(clientX) * fps));
    seekFrame(frame);
  }

  function handleTrackPointerDown(
    event: React.PointerEvent<HTMLDivElement>,
    clip: StudioClip,
    trackId: string,
    mode: DragState['mode']
  ) {
    event.stopPropagation();
    selectNode(clip.id);
    commitEdit();
    dragRef.current = {
      clipId: clip.id,
      trackId,
      mode,
      startX: event.clientX,
      origStart: clip.start,
      origDuration: clip.duration,
    };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag) return;
    const deltaSeconds = (event.clientX - drag.startX) / PX_PER_SECOND;
    if (drag.mode === 'move') {
      updateClip(drag.clipId, { start: snapTime(drag.origStart + deltaSeconds, fps) });
    } else if (drag.mode === 'trim-start') {
      const newStart = snapTime(drag.origStart + deltaSeconds, fps);
      updateClip(drag.clipId, {
        start: newStart,
        duration: Math.max(0.1, drag.origDuration - (newStart - drag.origStart)),
      });
    } else {
      updateClip(drag.clipId, {
        duration: Math.max(0.1, drag.origDuration + deltaSeconds),
      });
    }
  }

  function handlePointerUp() {
    dragRef.current = null;
  }

  function handleTrackDrop(event: React.DragEvent<HTMLDivElement>, trackId: string) {
    event.preventDefault();
    const assetId = event.dataTransfer.getData('application/x-asset-id');
    if (assetId) {
      const rect = event.currentTarget.getBoundingClientRect();
      const start = Math.max(0, pixelsToSeconds(event.clientX - rect.left - GUTTER));
      addClip(assetId, trackId, start);
      return;
    }
    if (event.dataTransfer.files.length > 0) {
      void importFiles(event.dataTransfer.files);
    }
  }

  const hasAnyClips = tracks.some((t) => t.clips.length > 0);

  return (
    <div className="timeline">
      <div className="transport">
        <button
          type="button"
          className="transport-btn primary-btn"
          onClick={togglePlay}
          disabled={!project && !hasAnyClips}
        >
          {isPlaying ? '⏸ Pause' : '▶ Play'}
        </button>
        <span className="transport-time">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
        <label className="transport-loop">
          <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} />
          <span>Loop</span>
        </label>
        <div className="transport-spacer" />
        <span className="transport-frame">frame {currentFrame}</span>
        <button
          type="button"
          className="ghost-btn"
          onClick={undo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
        >
          ↩ Undo
        </button>
        <button
          type="button"
          className="ghost-btn"
          onClick={redo}
          disabled={!canRedo}
          title="Redo (Ctrl+Shift+Z)"
        >
          ↪ Redo
        </button>
        <button type="button" className="ghost-btn" onClick={() => addTrack('video')}>
          + Video
        </button>
        <button type="button" className="ghost-btn" onClick={() => addTrack('audio')}>
          + Audio
        </button>
      </div>

      <div
        className="timeline-scroll"
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div className="timeline-ruler" style={{ width: totalWidth }}>
          <div className="ruler-gutter" style={{ width: GUTTER }}>
            Time
          </div>
          <div
            className="ruler-scale"
            onClick={(e) => seekFromEvent(e.clientX)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') seekFrame(currentFrame - 1);
              if (e.key === 'ArrowRight') seekFrame(currentFrame + 1);
            }}
            role="slider"
            tabIndex={0}
            aria-label="Timeline ruler"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration * fps)}
            aria-valuenow={currentFrame}
          >
            {Array.from({ length: Math.ceil(duration) + 1 }, (_, i) => i).map((second) => (
              <div
                key={`tick-${second}s`}
                className="ruler-tick"
                style={{ left: secondsToPixels(second) }}
              >
                <span>{second}s</span>
              </div>
            ))}
          </div>
        </div>

        {tracks.map((track) => (
          <div className="timeline-track" key={track.id} style={{ height: TRACK_HEIGHT }}>
            <div className="track-label" style={{ width: GUTTER }}>
              <span className="track-name">{track.name}</span>
              <span className="track-kind">{track.kind}</span>
            </div>
            <div
              className="track-body"
              style={{ width: Math.max(1, duration) * PX_PER_SECOND, position: 'relative' }}
              onPointerDown={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                seekFrame(Math.max(0, Math.round(pixelsToSeconds(e.clientX - rect.left) * fps)));
              }}
              onDrop={(e) => handleTrackDrop(e, track.id)}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }}
            >
              {track.clips.map((clip) => {
                const selected = clip.id === selectedNodeId;
                return (
                  <div
                    key={clip.id}
                    className={`clip${selected ? ' selected' : ''}`}
                    style={{
                      left: secondsToPixels(clip.start),
                      width: Math.max(4, secondsToPixels(clip.duration)),
                      backgroundColor: TRACK_COLORS[track.kind],
                    }}
                    title={`${assetName(clip.assetId)} — ${clip.start.toFixed(2)}s → ${(clip.start + clip.duration).toFixed(2)}s`}
                    onPointerDown={(e) => handleTrackPointerDown(e, clip, track.id, 'move')}
                  >
                    <span className="clip-name">{assetName(clip.assetId)}</span>
                    <div
                      className="clip-handle clip-handle-start"
                      onPointerDown={(e) => handleTrackPointerDown(e, clip, track.id, 'trim-start')}
                    />
                    <div
                      className="clip-handle clip-handle-end"
                      onPointerDown={(e) => handleTrackPointerDown(e, clip, track.id, 'trim-end')}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="playhead" style={{ left: GUTTER + currentTime * PX_PER_SECOND }} />
      </div>

      {!project && !hasAnyClips && (
        <div className="timeline-empty-overlay">Drop assets on a track to build your video</div>
      )}
    </div>
  );
}
